import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { compose } from 'redux';
import cn from 'classnames';
import forEach from 'lodash/forEach';
import PropTypes from 'prop-types';
import NotificationSystem from 'react-notification-system';
import SlidingPane from 'react-sliding-pane';
import Toggle from 'react-toggle';
import get from 'lodash/get';

import {
  getRefundReasons,
  getActiveContract,
  refundRequest,
} from 'actions/application';
import Input from 'components/Form/Input';
import Select from 'components/Form/Select';
import { Button } from 'components/Button';
import Validator from 'components/Validator/Validator';
import { formatCurrency } from 'utils/formatCurrency';
import { toTitleCase } from 'utils/toTitleCase';
import { unmaskCurrency, floatUnmask, currencyMask } from 'utils/masks';
import { parseUrlParams } from 'utils/parseUrlParams';
import closeIcon from 'assets/icons/arrow-left-grey.svg';
import schema from './schema';

const RequestRefundTray = (props) => {
  const { validator: { isValid, values, errors } } = props;
  const params = parseUrlParams(window.location.search);
  const dispatch = useDispatch();
  const [refund, setRefund] = useState(null);
  const [refundReasons, setRefundReasons] = useState([]);
  const [contract, setContract] = useState(null);
  const [isFull, setIsFull] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const form = useRef(null);
  const notificationSystem = useRef(null);

  const getCurrentAmountFinanced = () => {
    let currentAmountFinanced = 0;
    if (refund) {
      currentAmountFinanced = refund.newAmountFinanced || 0;
    } else if (contract) {
      currentAmountFinanced = contract.amountTaken || 0;
    }
    return parseFloat(currentAmountFinanced);
  };

  const handleToggleChange = (event) => {
    // eslint-disable-next-line prefer-destructuring
    const checked = event.target.checked;
    setIsFull(event.target.checked);
    if (checked) {
      const amountFinanced = getCurrentAmountFinanced().toString();
      const { validator: { onChangeHandler } } = props;
      onChangeHandler('refundAmount', formatCurrency(floatUnmask(amountFinanced || '0'), 2));
    }
  };

  const handleBlur = (event) => {
    event.preventDefault();
    const { validator: { onChangeHandler } } = props;
    switch (event.target.name) {
      case 'refundAmount':
        onChangeHandler(event.target.name, formatCurrency(floatUnmask(event.target.value || '0'), 2));
        break;
      default:
        onChangeHandler(event.target.name, toTitleCase(event.target.value));
        break;
    }
  };

  const handleInputChange = (event) => {
    const { validator: { onChangeHandler } } = props;
    const currentAmountFinanced = getCurrentAmountFinanced();

    switch (event.target.name) {
      case 'refundAmount':
        onChangeHandler(event.target.name, event.target.value);
        setIsFull(parseFloat(floatUnmask(event.target.value)) === currentAmountFinanced);
        break;
      default:
        onChangeHandler(event.target.name, event.target.value);
        break;
    }
  };

  const loadRefunds = () => {
    const merchantId = localStorage.getItem('merchantId');
    dispatch(getRefundReasons({
      url: `/refunds/search?merchantId=${merchantId}&applicationId=${params.applicationId}&refundStatus=6008&sortBy=createdOn&sortOrder=desc`,
      success: (res) => {
        if (res && res.data.length > 0) {
          setRefund(res.data[0]);
        }
      },
      fail: (err) => {
        if (err.response) {
          notificationSystem.current
          && notificationSystem.current.addNotification({
            message: 'fetching refund reasons',
            level: 'error',
            position: 'tc',
          });
        }
      },
    }));
  };

  const loadRefundReasons = () => {
    dispatch(getRefundReasons({
      url: '/lookup/refund-reasons',
      success: (res) => {
        const reasons = [];
        forEach(res, (reason) => {
          if (get(reason, 'attributes.channel') === 'fsl') {
            reasons.push({
              title: reason.label,
              value: reason.code,
            });
          }
        });
        setRefundReasons(reasons);
      },
      fail: (err) => {
        if (err.response) {
          notificationSystem.current
          && notificationSystem.current.addNotification({
            message: 'fetching refund reasons',
            level: 'error',
            position: 'tc',
          });
        }
      },
    }));
  };

  const loadActiveContract = () => {
    dispatch(getActiveContract({
      url: `/offers/application/${params.applicationId}/contract/active`,
      success: (res) => {
        setContract(res);
      },
      fail: (err) => {
        if (err.data) {
          notificationSystem.current
          && notificationSystem.current.addNotification({
            message: err.data.message,
            level: 'error',
            position: 'tc',
          });
        }
      },
    }));
  };

  const loadRefundData = () => {
    loadRefunds();
    loadRefundReasons();
    loadActiveContract();
  };

  useEffect(() => {
    loadRefundData();
  }, []);

  if (form && form.current && !isValid && isSubmitted) {
    const errs = Object.keys(errors);
    form.current[errs[0]].focus();
    setIsSubmitted(false);
  }

  const currentAmountFinanced = getCurrentAmountFinanced();

  const handleSubmitForm = (data, e) => {
    e.preventDefault();
    const { validator: { validate } } = props;

    if (validate(schema).isValid) {
      const formData = {
        ...data,
        refundAmount: floatUnmask(data.refundAmount),
        signature: data.signature,
      };

      if (params.applicationId) {
        setIsSubmitted(true);
        dispatch(refundRequest({
          url: '/refunds',
          data: {
            applicationId: params.applicationId,
            refundRequestedAmount: parseFloat(unmaskCurrency(formData.refundAmount)),
            refundReason: formData.reasonForRefund,
            signature: formData.signature,
            consentAutomaticDebit: true,
          },
          success: () => {
            props.onRequestClose();
            setIsSubmitted(false);
            notificationSystem.current
            && notificationSystem.current.addNotification({
              message: 'Refund request has been successfully submitted',
              level: 'success',
              position: 'tc',
            });
          },
          fail: (error) => {
            setIsSubmitted(false);
            notificationSystem.current
            && notificationSystem.current.addNotification({
              message: (error.data && error.data.message) || 'Refund request submission failed',
              level: 'error',
              position: 'tc',
            });
          },
        }));
      }
    }
  };

  return (
    <SlidingPane
      isOpen={props.isOpen}
      closeIcon={<img src={closeIcon} alt="close" />}
      title="Return of Funds"
      width="460px"
      onRequestClose={props.onRequestClose}
      from="right"
    >
      <NotificationSystem ref={notificationSystem} />
      <form className={cn('requestRefund')} onSubmit={handleSubmitForm.bind(null, values)} ref={form}>
        <div className="grid-x grid-margin-x d-flex flex-column" style={{ minHeight: '100%' }}>
          <div className="cell small-12">
            <Input
              type="tel"
              label="Return Amount"
              name="refundAmount"
              value={values.refundAmount}
              isMasked={currencyMask}
              onChange={handleInputChange}
              hasError={!!errors.refundAmount}
              isRequired
              onBlur={handleBlur}
              errorMessage={errors.refundAmount}
            />
          </div>
          <div className="cell small-12 mb-4">
            <div className="containerToggle d-flex">
              <Toggle
                checked={isFull}
                icons={false}
                onChange={handleToggleChange}
              />
              <span style={{ marginLeft: 5 }}>Full</span>
            </div>
          </div>
          <div className="cell small-12 mb-5">
            <Select
              name="reasonForRefund"
              data={refundReasons}
              value={values.reasonForRefund}
              onChange={handleInputChange}
              label="Reason for Refund"
              isRequired
              hasError={!!errors.reasonForRefund}
              errorMessage={errors.reasonForRefund}
            />
          </div>
          <div className="cell small-12">
            <label className="has-value">
              <span>Current Amount Financed</span>
              <input
                type="text"
                value={`$ ${formatCurrency(currentAmountFinanced, 2)}`}
                readOnly
              />
            </label>
          </div>
          <div className="cell small-12">
            <Input
              label="Signature - Type Full Name"
              name="signature"
              value={values.signature}
              onChange={handleInputChange}
              isRequired
              hasError={!!errors.signature}
              onBlur={handleBlur}
              errorMessage={errors.signature}
            />
          </div>
          <div className="cell small-12 mt-auto">
            <Button
              id="SubmitRefundRequest"
              className="button green w-100"
              isLoading={isSubmitted}
            >
              Return Funds
            </Button>
          </div>
        </div>
      </form>
    </SlidingPane>
  );
};

RequestRefundTray.propTypes = {
  validator: PropTypes.object.isRequired,
  onRequestClose: PropTypes.func,
  isOpen: PropTypes.bool,
};

RequestRefundTray.defaultProps = {
  isOpen: false,
  onRequestClose: () => {},
};

export default compose(Validator(schema))(RequestRefundTray);



// WEBPACK FOOTER //
// ./src/components/RequestRefundTray/index.js